// Social-share helpers shared by every map type (geoshaped photos + travel maps).
//
// Social networks cannot attach an uploaded image to a share — they scrape the
// shared link for Open Graph tags. So the map image is uploaded to Firebase
// Storage and wrapped in a share page (the `shareCard` Cloud Function at
// map-shaped.com/s) whose OG tags point at that image. Sharing that page makes
// the network preview show the real map.

export const SITE_URL = 'https://map-shaped.com';

// Minimal caption style. `title` is the clean headline (used for the share
// page / link-preview card); `caption` adds the "Made at …" line for the
// post body text.
export const buildTravelCaption = ({ region, visited, total, label }) => {
    const title = `My ${region} travel map — ${visited}/${total} ${label}`;
    return { title, caption: `${title}. Made at ${SITE_URL}` };
};

export const buildShaperCaption = ({ shapeName }) => {
    const title = `My ${shapeName}-shaped photo`;
    return { title, caption: `${title}. Made at ${SITE_URL}` };
};

export const buildTrailCaption = ({ title, distance, days }) => {
    const head = title || 'My hike';
    const bits = [distance, days > 1 ? `${days} days` : null].filter(Boolean).join(' · ');
    const t = bits ? `${head} — ${bits}` : head;
    return { title: t, caption: `${t}. Mapped at ${SITE_URL}` };
};

export const buildRoadTripCaption = ({ title, stops, distance }) => {
    const head = title || 'My road trip';
    const bits = [stops ? `${stops} stops` : null, distance].filter(Boolean).join(' · ');
    const t = bits ? `${head} — ${bits}` : head;
    return { title: t, caption: `${t}. Mapped at ${SITE_URL}` };
};

const enc = encodeURIComponent;

// Each network opens a web "share intent". `pageUrl` is the share page (its OG
// tags drive the preview image); `caption` is the post body text.
export const SHARE_NETWORKS = [
    {
        id: 'facebook',
        label: 'Facebook',
        color: '#1877F2',
        buildUrl: ({ pageUrl }) =>
            `https://www.facebook.com/sharer/sharer.php?u=${enc(pageUrl)}`,
    },
    {
        id: 'x',
        label: 'X',
        color: '#000000',
        buildUrl: ({ pageUrl, caption }) =>
            `https://twitter.com/intent/tweet?text=${enc(caption)}&url=${enc(pageUrl)}`,
    },
    {
        id: 'reddit',
        label: 'Reddit',
        color: '#FF4500',
        buildUrl: ({ pageUrl, caption }) =>
            `https://www.reddit.com/submit?url=${enc(pageUrl)}&title=${enc(caption)}`,
    },
    {
        id: 'bluesky',
        label: 'Bluesky',
        color: '#0085FF',
        buildUrl: ({ pageUrl, caption }) =>
            `https://bsky.app/intent/compose?text=${enc(`${caption}\n\n${pageUrl}`)}`,
    },
    {
        id: 'tumblr',
        label: 'Tumblr',
        color: '#34526F',
        buildUrl: ({ pageUrl, caption }) =>
            `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${enc(pageUrl)}&caption=${enc(caption)}`,
    },
];

// True when the browser can share the actual image file through the OS share
// sheet. This is the only path that reaches Instagram / TikTok / Messages with
// the image attached.
export const canWebShareFiles = (files) => {
    try {
        return (
            typeof navigator !== 'undefined' &&
            typeof navigator.canShare === 'function' &&
            navigator.canShare({ files })
        );
    } catch {
        return false;
    }
};

// True only on phones / tablets. The native share sheet is worth using there
// (it carries the image into Instagram, WhatsApp, etc.), but on desktop it
// only offers Mail/Messages/AirDrop — so desktop always uses the custom dialog.
export const isMobileShareDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/Android|iPhone|iPod|iPad|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
        return true;
    }
    // iPadOS 13+ reports a desktop user agent but still exposes touch points.
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
};

/**
 * Flatten a (possibly transparent) image blob onto a solid background and
 * re-encode as JPEG.
 *
 * The download / product-upload pipeline uses transparent PNG so prints look
 * right on dark cups and t-shirts. Social-media card crawlers (Bluesky, X,
 * Reddit, Facebook) don't reliably render transparent PNGs as og:image — they
 * tend to skip them, composite on black, or render the alpha as opaque grey.
 * For the share-card image we therefore flatten onto a fixed background and
 * publish a JPEG.
 *
 *   const flat = await flattenForShare(transparentPng);  // -> opaque jpeg Blob
 */
export const flattenForShare = async (blob, { background = '#ffffff', quality = 0.92 } = {}) => {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close && bitmap.close();
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (jpegBlob) => (jpegBlob ? resolve(jpegBlob) : reject(new Error('toBlob failed'))),
            'image/jpeg',
            quality
        );
    });
};
